import i18n from '../i18n'

/**
 * The notifications screen's rules (design page 15, `notifs`), mirrored
 * from the Android app's `Inbox.kt` / `NotifsScreen.kt` so both clients
 * file a row under the same chip, colour it the same, and send a tap to
 * the same place. Plain functions, not components, so they read
 * `i18n.t()` directly rather than the `useTranslation()` hook — the
 * `notifications` namespace.
 */

const t = (key, options) => i18n.t(key, { ns: 'notifications', ...options })

/** A function, not a constant: the active language decides the labels. The filter chips: everything, then one per kind. */
export const KIND_FILTERS = () => [
  { key: null, label: t('kindFilters.all') },
  { key: 'result', label: t('kindFilters.result') },
  { key: 'streak', label: t('kindFilters.streak') },
  { key: 'pay', label: t('kindFilters.pay') },
  { key: 'social', label: t('kindFilters.social') },
]

/** The 38px tile's tint per kind: analysis blue, reminder gold, payment green, social blue. */
export const KIND_TILE = {
  result: 'bg-brand-50 text-brand-700',
  streak: 'bg-warn-tint text-warn-ink',
  pay: 'bg-good-tint text-good-ink',
  social: 'bg-brand-50 text-brand-700',
}

/**
 * Where a tap leads. The server sends the target, the client resolves the
 * screen — and this client has fewer screens than the design yet:
 * `autopay` lands on the profile until P-autopay; `paywall` (the trial
 * reminders, tooti#121) on the purchase page.
 */
export const TARGET_ROUTE = {
  home: '/dashboard',
  archive: '/archive',
  progress: '/progress',
  autopay: '/profile',
  feed: '/social',
  paywall: '/paywall',
}

export const TARGET_CTA = {
  get home() {
    return t('targetCta.home')
  },
  get archive() {
    return t('targetCta.archive')
  },
  get progress() {
    return t('targetCta.progress')
  },
  get autopay() {
    return t('targetCta.autopay')
  },
  get feed() {
    return t('targetCta.feed')
  },
  get paywall() {
    return t('targetCta.paywall')
  },
}

/** A function, not a constant: the active language decides the labels. The five channel switches, in the design's order, with their notes. */
export const CHANNELS = () => [
  { key: 'practice', label: t('channels.practice.label'), note: t('channels.practice.note') },
  { key: 'result', label: t('channels.result.label'), note: t('channels.result.note') },
  { key: 'social', label: t('channels.social.label'), note: t('channels.social.note') },
  { key: 'pay', label: t('channels.pay.label'), note: t('channels.pay.note') },
  { key: 'offers', label: t('channels.offers.label'), note: t('channels.offers.note') },
]

export function filterByKind(items, kind) {
  return kind ? items.filter((item) => item.kind === kind) : items
}

export function unreadCountOf(items) {
  return items.filter((item) => !item.readAt).length
}

/** The optimistic local half of mark-read: the rows the server is about to confirm. */
export function markReadLocally(items, { ids, all } = {}, now = new Date()) {
  const at = now.toISOString()
  const chosen = all ? null : new Set(ids ?? [])
  return items.map((item) => (item.readAt || (chosen && !chosen.has(item.id)) ? item : { ...item, readAt: at }))
}
